"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ListItemElement = exports.ListContainerElement = void 0;
const tslib_1 = require("tslib");
const React = tslib_1.__importStar(require("react"));
const nano_theme_1 = require("nano-theme");
const slate_1 = require("slate");
const slate_react_1 = require("slate-react");
const behavior_1 = require("../../behavior");
const BlockPlaceholder_1 = require("./BlockPlaceholder");
const font_1 = require("../../behavior/font");
const css_1 = require("../../custom-style/css");
const util_1 = require("../../util");
const StepperItem_1 = require("../../block/stepper/StepperItem");
const ProgressCap_1 = require("../../block/stepper/ProgressCap");
const listClass = (0, nano_theme_1.rule)({
    mr: '0 0 16px',
    paddingInlineStart: '24px',
});
const checklistClass = (0, nano_theme_1.rule)({
    mr: '0 0 16px',
    paddingInlineStart: '24px',
    listStyle: 'none',
});
const itemClass = (0, nano_theme_1.rule)({
    pos: 'relative',
    mr: '1px 0',
    lh: (0, css_1.lhVar)('1.7'),
});
const checklistItemClass = (0, nano_theme_1.rule)({
    pos: 'relative',
    mr: '1px 0',
    lh: (0, css_1.lhVar)('1.7'),
    listStyle: 'none',
});
const checkboxWrapClass = (0, nano_theme_1.rule)({
    pos: 'absolute',
    insetInlineStart: '-24px',
    t: '6px',
    d: 'inline-flex',
    ai: 'center',
    jc: 'center',
    flexShrink: 0,
});
const checkboxClass = (0, nano_theme_1.rule)({
    w: '16px',
    h: '16px',
    mar: 0,
    bd: '1px solid rgba(127,127,127,0.4)',
    bdrad: '4px',
    appearance: 'none',
    d: 'grid',
    placeContent: 'center',
    cur: 'pointer',
    '&::before': {
        content: '""',
        w: '12px',
        h: '12px',
        trs: '120ms transform ease-in-out',
        bg: '#fff',
        clipPath: 'polygon(14% 44%, 0 65%, 50% 100%, 100% 16%, 80% 0%, 43% 62%)',
        transform: 'scale(0)',
    },
    '&:checked': {
        bg: '#2f8f4e',
        borderColor: '#2f8f4e',
    },
    '&:checked::before': {
        transform: 'scale(.7)',
    },
});
const checklistContentClass = (0, nano_theme_1.rule)({
    d: 'block',
    minW: '0',
});
const stepperClass = (0, nano_theme_1.rule)({
    mr: '0 0 16px',
    pad: '4px 0 0 0',
    listStyle: 'none',
});
const ListContainerElement = ({ attributes, children, element }) => {
    if (element.type === 'checklist') {
        return (React.createElement("ul", { ...attributes, className: checklistClass }, children));
    }
    if (element.type === 'stepper') {
        return (React.createElement("ol", { ...attributes, className: stepperClass, "aria-label": "Stepper" },
            element.progress ? React.createElement(ProgressCap_1.ProgressCap, { element: element }) : null,
            children));
    }
    if (element.type === 'ol') {
        return (React.createElement("ol", { ...attributes, className: listClass, style: { paddingInlineStart: '26px', listStyleType: element.olType ?? 'decimal' } }, children));
    }
    return (React.createElement("ul", { ...attributes, className: listClass, style: { listStyleType: element.ulType ?? 'disc' } }, children));
};
exports.ListContainerElement = ListContainerElement;
const ListItemElement = ({ attributes, children, element }) => {
    const editor = (0, slate_react_1.useSlateStatic)();
    const readOnly = (0, slate_react_1.useReadOnly)();
    const parentType = React.useMemo(() => {
        try {
            const path = slate_react_1.ReactEditor.findPath(editor, element);
            const [parent] = slate_1.Editor.parent(editor, path);
            return slate_1.Element.isElement(parent) ? parent.type : null;
        }
        catch {
            return null;
        }
    }, [editor, element]);
    const isChecklistItem = parentType === 'checklist';
    const isStepperItem = parentType === 'stepper';
    const onToggle = React.useCallback((event) => {
        event.preventDefault();
        if (readOnly || !isChecklistItem)
            return;
        const path = slate_react_1.ReactEditor.findPath(editor, element);
        (0, behavior_1.setChecklistItemChecked)(editor, path, !element.checked);
    }, [editor, element, isChecklistItem, readOnly]);
    if (isStepperItem) {
        return (React.createElement(StepperItem_1.StepperItem, { attributes: attributes, element: element }, children));
    }
    if (!isChecklistItem) {
        return (React.createElement("li", { ...attributes, className: itemClass, style: { textAlign: element.align, fontFamily: (0, font_1.fontFamilyOf)(element.font) } },
            children,
            (0, util_1.isEmptyBlock)(element) && React.createElement(BlockPlaceholder_1.BlockPlaceholder, { element: element })));
    }
    return (React.createElement("li", { ...attributes, className: checklistItemClass, style: { textAlign: element.align, fontFamily: (0, font_1.fontFamilyOf)(element.font) } },
        React.createElement("span", { className: checkboxWrapClass, contentEditable: false },
            React.createElement("input", { className: checkboxClass, type: "checkbox", checked: !!element.checked, readOnly: true, disabled: readOnly, tabIndex: -1, onMouseDown: onToggle })),
        React.createElement("span", { className: checklistContentClass, style: {
                textDecoration: element.checked ? 'line-through' : undefined,
                opacity: element.checked ? 0.68 : 1,
            } },
            children,
            (0, util_1.isEmptyBlock)(element) && React.createElement(BlockPlaceholder_1.BlockPlaceholder, { element: element }))));
};
exports.ListItemElement = ListItemElement;
